import { BookOpen, LogOut, Moon, Sun, Dumbbell, BarChart3, Shield, Gift, Layers, ClipboardList, CalendarDays, Menu, Settings, LifeBuoy, Receipt, LogIn, ScanLine, ShieldCheck, Brain, CalendarClock, Trophy, Users, GraduationCap, Handshake, Tag, Image as ImageIcon, Presentation } from "lucide-react";
import { Link } from "@tanstack/react-router";
import type { User } from "@supabase/supabase-js";
import { useTheme } from "@/hooks/useTheme";
import { LanguageSelector } from "@/components/LanguageSelector";
import { PremiumBadge } from "@/components/PremiumBadge";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { useEducation } from "@/hooks/useEducation";
// Primary source: the Vite-imported PNG, emitted as a content-hashed file under
// /assets/ so it is cached and served like every other build asset (including
// from the Capacitor local origin). The base64 data URI is kept only as an
// in-bundle fallback that the <img> swaps to if the hashed URL ever fails to
// load, so the header can never show a broken-image box.
import inquiraLogoUrl from "@/assets/inquira-logo.png";
import inquiraLogoInline from "@/assets/inquira-logo-inline";
import { trackLogoRender } from "@/lib/analytics";


import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface AppHeaderProps {
  /** @deprecated Streak is shown via <FloatingStreak /> on the left edge. */
  streak?: number;
  user?: User | null;
  isPaid?: boolean;
  onSignOut?: () => void;
}

export function AppHeader({ user, isPaid, onSignOut }: AppHeaderProps) {
  const { isDark, toggle } = useTheme();
  const { t } = useTranslation();
  const { isTeacher: eduIsTeacher, teacherPortalEnabled, hasChosenRole } = useEducation();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  // Fallback chain: hashed asset -> inline data URI -> wordmark only.
  const [logoSrc, setLogoSrc] = useState<string>(inquiraLogoUrl);
  const [logoFailed, setLogoFailed] = useState(false);

  const handleLogoLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const usingFallback = logoSrc === inquiraLogoInline;
    // Console line is intentional: it is how the resolved src is inspected on a
    // real production Android/iOS device via remote WebView debugging.
    console.info("[inquira-logo] loaded", {
      resolvedSrc: img.currentSrc || img.src,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      usingFallback,
    });
    trackLogoRender({
      ok: true,
      src: usingFallback ? "inline-data-uri" : img.currentSrc || img.src,
      natural_width: img.naturalWidth,
      natural_height: img.naturalHeight,
      using_fallback: usingFallback,
    });
  };

  const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const attempted = e.currentTarget.currentSrc || e.currentTarget.src || logoSrc;
    const willFallback = logoSrc !== inquiraLogoInline;
    console.error("[inquira-logo] failed to load", {
      attemptedSrc: attempted,
      reason: willFallback ? "hashed-asset-unresolvable" : "inline-data-uri-decode-failed",
      willFallback,
    });
    trackLogoRender({
      ok: false,
      src: attempted,
      reason: willFallback ? "hashed-asset-unresolvable" : "inline-data-uri-decode-failed",
      using_fallback: !willFallback,
    });
    if (willFallback) setLogoSrc(inquiraLogoInline);
    else setLogoFailed(true);
  };


  useEffect(() => {
    if (!user) { setIsSuperAdmin(false); return; }
    let active = true;
    supabase
      .rpc("has_role" as never, { _user_id: user.id, _role: "super_admin" } as never)
      .then(({ data }) => { if (active) setIsSuperAdmin(data === true); });
    return () => { active = false; };
  }, [user]);

  // Drives whether the menu offers the creator dashboard or the join page.
  useEffect(() => {
    if (!user) { setIsAffiliate(false); return; }
    let active = true;
    supabase
      .from("affiliates" as never)
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => { if (active) setIsAffiliate(!!data); });
    return () => { active = false; };
  }, [user]);

  const publicItems = [
    { to: "/pricing", label: t("nav.pricing", "Plans & Pricing"), icon: Tag },
    { to: "/courses", label: t("nav.courses"), icon: GraduationCap },
    { to: "/coach", label: t("nav.coach"), icon: Brain },
    { to: "/progress", label: t("nav.progress"), icon: BarChart3 },
    { to: "/practice", label: t("nav.practice"), icon: Dumbbell },
    { to: "/history", label: t("nav.history"), icon: BookOpen },
    { to: "/support", label: t("nav.support"), icon: LifeBuoy },
  ] as const;
  const userItems = [
    { to: "/usage", label: t("nav.usage"), icon: ScanLine },
    { to: "/game", label: t("nav.game"), icon: Trophy },
    { to: "/friends", label: t("nav.friends"), icon: Users },
    { to: "/family", label: t("nav.family"), icon: GraduationCap },
    { to: "/exam", label: t("nav.exam"), icon: CalendarClock },
    { to: "/flashcards", label: t("nav.flashcards"), icon: Layers },
    { to: "/quiz", label: t("nav.quiz"), icon: ClipboardList },
    { to: "/planner", label: t("nav.planner"), icon: CalendarDays },
    { to: "/originality", label: t("nav.originality"), icon: ShieldCheck },
    { to: "/referrals", label: t("nav.referrals"), icon: Gift },
    { to: "/settings", label: t("nav.settings"), icon: Settings },
    { to: "/icon-diagnostics", label: t("nav.iconDiagnostics", "Icon diagnostics"), icon: ImageIcon },
  ] as const;

  return (
    <header
      className="safe-top flex flex-col items-center gap-1 pb-1 px-2 overflow-visible"
      // Android/iOS native wrappers draw under the status bar; without this the
      // top of the logo (bulb + rays) gets clipped by the system inset.
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}
    >
      <Link
        to="/"
        aria-label={t("header.homeLabel")}
        className="flex max-w-full items-center gap-0.5 sm:gap-1 group overflow-visible leading-none"
      >
        {!logoFailed && (
          // Sized by an aspect-ratio box instead of width/height attributes:
          // some Android WebViews laid the raw 419px intrinsic width out first,
          // which overflowed narrow (320-412px) rows and clipped the artwork.
          <img
            src={logoSrc}
            alt=""
            aria-hidden="true"
            data-testid="inquira-logo"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            onLoad={handleLogoLoad}
            onError={handleLogoError}

            style={{ objectFit: "contain", objectPosition: "center", aspectRatio: "419 / 344" }}
            className="block h-9 sm:h-12 md:h-16 w-auto shrink-0 drop-shadow-sm transition-transform group-hover:scale-[1.03]"
          />
        )}
        <span
          className={`font-display font-black tracking-tighter text-[clamp(1.9rem,11vw,2.25rem)] sm:text-5xl md:text-6xl leading-none ${isDark ? "text-white" : "text-black"}`}
        >
          Inquira
        </span>
      </Link>
      <div className="flex items-center gap-1.5 sm:gap-2 justify-center max-w-full">
        {isPaid && <PremiumBadge size="xs" className="shrink-0" />}
        <LanguageSelector isPaid={isPaid} />
        <button
          onClick={toggle}
          className="size-8 sm:size-9 rounded-full bg-card border border-rule flex items-center justify-center hover:bg-rule/30 transition-colors shrink-0"
          title={t("header.toggleDarkMode")}
          aria-label={t("header.toggleDarkMode")}
        >
          {isDark ? <Sun className="size-3.5 sm:size-4 text-neon-yellow" /> : <Moon className="size-3.5 sm:size-4 text-ink-muted" />}
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger
            className="h-8 sm:h-9 px-3 rounded-full bg-card border border-rule flex items-center gap-1.5 hover:bg-rule/30 transition-colors shrink-0"
            aria-label={t("header.openMenu")}
          >
            <Menu className="size-3.5 sm:size-4 text-ink-muted" />
            <span className="text-xs sm:text-sm font-semibold text-ink">{t("header.menu")}</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 z-[100]">
            <DropdownMenuLabel className="text-neon-pink">{t("header.explore")}</DropdownMenuLabel>
            {publicItems.map(({ to, label, icon: Icon }) => (
              <DropdownMenuItem key={to} asChild>
                <Link to={to} className="flex items-center gap-2 cursor-pointer">
                  <Icon className="size-4 text-ink-muted" />
                  <span>{label}</span>
                </Link>
              </DropdownMenuItem>
            ))}
            {user && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-neon-pink">{t("header.studyTools")}</DropdownMenuLabel>
                {userItems.map(({ to, label, icon: Icon }) => (
                  <DropdownMenuItem key={to} asChild>
                    <Link to={to} className="flex items-center gap-2 cursor-pointer">
                      <Icon className="size-4 text-ink-muted" />
                      <span>{label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <Link to="/billing" className="flex items-center gap-2 cursor-pointer">
                    <Receipt className="size-4 text-ink-muted" />
                    <span>{t("nav.billing")}</span>
                  </Link>
                </DropdownMenuItem>
                {/* Classroom links only appear when the classroom feature is
                    switched on for this account, so the menu never points at a
                    flag-gated "not enabled" screen. */}
                {/* Also shown to anyone who already has a classroom role (e.g. a
                    student who joined a class with a code), so they never lose the
                    way back to their classes when the flag is off. */}
                {(teacherPortalEnabled || eduIsTeacher || isSuperAdmin || hasChosenRole) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-neon-pink">{t("nav.classroom", "Classroom")}</DropdownMenuLabel>
                    <DropdownMenuItem asChild>
                      <Link to="/classes" search={{ join: undefined }} className="flex items-center gap-2 cursor-pointer">
                        <GraduationCap className="size-4 text-ink-muted" />
                        <span>{t("nav.myClasses", "My classes")}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/assignments" className="flex items-center gap-2 cursor-pointer">
                        <ClipboardList className="size-4 text-ink-muted" />
                        <span>{t("nav.assignments", "Assignments")}</span>
                      </Link>
                    </DropdownMenuItem>
                    {(eduIsTeacher || isSuperAdmin) && (
                      <DropdownMenuItem asChild>
                        <Link to="/teacher" className="flex items-center gap-2 cursor-pointer">
                          <Presentation className="size-4 text-ink-muted" />
                          <span>{t("nav.teacherPortal", "Teacher portal")}</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}

              </>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-neon-pink">{t("nav.creatorProgram", "Creator program")}</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link
                to={user && isAffiliate ? "/affiliate" : "/affiliates"}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Handshake className="size-4 text-ink-muted" />
                <span>
                  {user && isAffiliate
                    ? t("nav.affiliateDashboard", "Affiliate dashboard")
                    : t("nav.becomeAffiliate", "Become an affiliate")}
                </span>
              </Link>
            </DropdownMenuItem>
            {isSuperAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                    <Shield className="size-4 text-neon-pink" />
                    <span className="font-semibold">{t("nav.adminDashboard")}</span>
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            {user && onSignOut && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); onSignOut(); }}
                  className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
                >
                  <LogOut className="size-4" />
                  <span>{t("common.signOut")}</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {!user && (
          <Link
            to="/login"
            className="h-8 sm:h-9 px-3 rounded-full bg-primary text-primary-foreground border border-primary flex items-center gap-1.5 hover:opacity-90 transition-opacity shrink-0"
            aria-label={t("common.signInOrSignUp")}
          >
            <LogIn className="size-3.5 sm:size-4" />
            <span className="text-xs sm:text-sm font-semibold whitespace-nowrap">{t("common.signIn")}</span>
          </Link>
        )}
      </div>
    </header>
  );
}
